import React from 'react';
import useFadeIn from '../hooks/useFadeIn';
import { useLang } from '../LanguageContext';
import './Products.css';

const T = {
  heading: { ko: '제품소개', en: 'Products' },
  desc:    {
    ko: <>스마트 기저귀 센서부터 요양시설 대시보드, 보호자 앱까지<br />통합 헬스케어 솔루션을 제공합니다</>,
    en: <>From smart diaper sensors to facility dashboards and caregiver apps,<br />we provide integrated healthcare solutions.</>,
  },
  items: [
    {
      title: { ko: '스마트 기저귀 센서', en: 'Smart Diaper Sensor' },
      desc:  { ko: '기저귀에 부착하는 초소형 IoT 센서로 배뇨·배변 감지, 체위 변환 패턴 분석을 동시에 수행합니다',
               en: 'A compact IoT sensor attached to diapers — detects urination/bowel movements and analyzes posture-change patterns simultaneously.' },
      features: {
        ko: ['LoRa 장거리 무선 통신', '배뇨/배변 실시간 감지', '의료등급 방수 설계'],
        en: ['LoRa long-range wireless', 'Real-time urination/bowel detection', 'Medical-grade waterproof design'],
      },
    },
    {
      title: { ko: '요양시설 케어 대시보드', en: 'Care Facility Dashboard' },
      desc:  { ko: '요양원·요양병원 관리자가 모든 입소자의 기저귀 상태와 건강 데이터를 한눈에 확인할 수 있는 웹 대시보드',
               en: 'A web dashboard for nursing home and care hospital managers to monitor every resident’s diaper status and health data at a glance.' },
      features: {
        ko: ['배뇨/배변 실시간 알림', '입소자별 헬스케어 리포트', '시설 전체 통계 분석'],
        en: ['Real-time urination/bowel alerts', 'Per-resident healthcare reports', 'Facility-wide statistics & analytics'],
      },
    },
    {
      title: { ko: '보호자 모바일 앱', en: 'Caregiver Mobile App' },
      desc:  { ko: '보호자와 간병인이 스마트폰으로 어르신의 기저귀 교체 알림과 건강 상태를 실시간으로 확인할 수 있는 앱',
               en: 'A smartphone app letting families and caregivers receive diaper-change alerts and check elderly residents’ health in real time.' },
      features: {
        ko: ['기저귀 교체 푸시 알림', '일별/주별 케어 이력', '가족 보호자 공유'],
        en: ['Diaper-change push notifications', 'Daily/weekly care history', 'Family sharing'],
      },
    },
    {
      title: { ko: '태블릿 앱', en: 'Tablet App' },
      desc:  { ko: '요양시설 현장에서 태블릿으로 입소자 상태를 실시간 확인하고 관리할 수 있는 대시보드형 앱',
               en: 'A dashboard-style tablet app for on-site staff to monitor and manage resident status in real time.' },
      features: {
        ko: ['실시간 입소자 상태 모니터링', '기저귀 교체 알림 및 기록', '시설 현장 맞춤 대시보드'],
        en: ['Real-time resident monitoring', 'Diaper-change alerts & records', 'Field-optimized dashboard'],
      },
    },
  ],
};

const productIcons = [
  (<svg viewBox="0 0 80 80" fill="none">
    <rect x="15" y="10" width="50" height="60" rx="8" stroke="currentColor" strokeWidth="2"/>
    <circle cx="40" cy="35" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M25 55h30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M30 62h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>),
  (<svg viewBox="0 0 80 80" fill="none">
    <rect x="10" y="15" width="60" height="40" rx="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M10 25h60" stroke="currentColor" strokeWidth="2"/>
    <rect x="15" y="30" width="25" height="15" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M30 60h20v5H30z" stroke="currentColor" strokeWidth="2"/>
  </svg>),
  (<svg viewBox="0 0 80 80" fill="none">
    <rect x="20" y="5" width="40" height="70" rx="8" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 15h40M20 60h40" stroke="currentColor" strokeWidth="2"/>
    <circle cx="40" cy="65" r="3" stroke="currentColor" strokeWidth="2"/>
    <rect x="28" y="25" width="24" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M28 40h24M28 47h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>),
  (<svg viewBox="0 0 80 80" fill="none">
    <rect x="12" y="10" width="56" height="60" rx="6" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 18h56M12 62h56" stroke="currentColor" strokeWidth="2"/>
    <circle cx="40" cy="66" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="18" y="24" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="42" y="24" width="20" height="6" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="42" y="34" width="20" height="4" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M18 44h44M18 50h30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>),
];

function Products() {
  const ref = useFadeIn();
  const { lang } = useLang();

  return (
    <section className="section products">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">PRODUCTS</span>
          <h2>{T.heading[lang]}</h2>
          <p className="section-desc">{T.desc[lang]}</p>
        </div>
        <div className="product-grid" ref={ref}>
          {T.items.map((p, i) => (
            <div className="product-card" key={i}>
              <div className="product-img">
                <div className="product-placeholder">{productIcons[i]}</div>
              </div>
              <div className="product-info">
                <h3>{p.title[lang]}</h3>
                <p>{p.desc[lang]}</p>
                <ul className="product-features">
                  {p.features[lang].map((f, j) => (
                    <li key={j}>{f}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Products;
